import React, { useEffect, useState } from 'react';
import { SafetyContent } from './AdminSafety';
import AdminLayout from './AdminLayout';
import { apiFetch, getToken, API_BASE, photoUrl } from '../../services/apiClient';
import { fmtDate, jobLabel } from '../../utils/ui';
import { JHA_TYPES, JHA_TEMPLATES } from '../../jhaTemplates';
import { SAFETY_POLICY_LIBRARY } from '../../safetyPolicyLibrary';

// The Safety tab is its own card hub with three sections.
const SAFETY_CARDS = [
  { key: 'training',  label: 'Safety Training',     icon: '🎓', color: '#f97316', sub: 'Assignments & repository' },
  { key: 'documents', label: 'Safety Documents',    icon: '📄', color: '#1e3a5f', sub: 'Upload & crew sign-off' },
  { key: 'jha',       label: 'Job Hazard Analyses', icon: '⚠️', color: '#b45309', sub: 'Per-task hazards & controls' },
];

export function SafetySection() {
  const [sub, setSub] = useState(null);
  const active = SAFETY_CARDS.find(c => c.key === sub);

  if (!active) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
        {SAFETY_CARDS.map(c => (
          <button key={c.key} onClick={() => setSub(c.key)}
            style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${c.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .15s, transform .1s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ fontSize: 30 }}>{c.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{c.label}</div>
            <div style={{ fontSize: 13, color: c.color, fontWeight: 600 }}>{c.sub}</div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setSub(null)}
        style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        ‹ Safety
      </button>
      {sub === 'training' && <SafetyContent embedded />}
      {sub === 'documents' && <SafetyDocuments />}
      {sub === 'jha' && <JobHazardAnalyses />}
    </div>
  );
}

// Safety as its own page in the side menu, right under Shop (Pat, Sep 2026). It used to
// be a tile inside the Shop page; the three cards and everything behind them are the
// same — this is only where you get to them from. The field's Shop hub on the phone
// (SupShop) still carries its own Safety tile.
export default function AdminSafetyPage() {
  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Safety</h1>
          <p className="admin-page-sub">Training, safety documents &amp; job hazard analyses</p>
        </div>
      </div>
      <SafetySection />
    </AdminLayout>
  );
}

const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const lblS = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };

// ── Safety Documents ────────────────────────────────────────────────────────
// Documents live in one of three buckets, each its own card. Legacy/free-text
// categories are matched loosely so nothing gets stranded.
const DOC_CATEGORIES = [
  { key: 'Employee Handbook', label: 'Titan Employee Handbook', icon: '📘', color: '#1e3a5f', sub: 'Company handbook & acknowledgements' },
  { key: 'Safety Policies',   label: 'Safety Policies',         icon: '🛡️', color: '#b91c1c', sub: 'Policies crews read & sign' },
  { key: 'SDS Sheets',        label: 'SDS Sheets',              icon: '🧪', color: '#15803d', sub: 'Safety Data Sheets' },
];
function bucketOf(cat) {
  const c = (cat || '').toLowerCase();
  if (c.includes('sds') || c.includes('data sheet')) return 'SDS Sheets';
  if (c.includes('handbook') || c.includes('employee')) return 'Employee Handbook';
  return 'Safety Policies'; // default catch-all (policies, forms, blank, …)
}

function SafetyDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', category: '', description: '', body: '', file: null });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState(null); // selected category card
  const [libOpen, setLibOpen] = useState(false); // Safety Policy Library picker
  const [reading, setReading] = useState(null);  // doc whose body we're viewing

  function reload() { apiFetch('/safety-documents').then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => {}); }
  useEffect(() => { apiFetch('/safety-documents').then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  async function upload() {
    if (!form.title.trim() && !form.file) { setErr('Add a title or a file'); return; }
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('title', form.title || (form.file ? form.file.name : '')); fd.append('category', form.category || ''); fd.append('description', form.description || ''); fd.append('body', form.body || '');
      if (form.file) fd.append('file', form.file);
      await fetch(`${API_BASE}/safety-documents`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      reload(); setAdding(false); setForm({ title: '', category: '', description: '', body: '', file: null });
    } catch (e) { setErr('Upload failed'); }
    setSaving(false);
  }
  async function del(id) { if (!window.confirm('Delete this document and its signatures?')) return; try { await apiFetch(`/safety-documents/${id}`, { method: 'DELETE' }); setDocs(p => p.filter(x => x.id !== id)); } catch {} }
  async function openSigs(doc) { try { const s = await apiFetch(`/safety-documents/${doc.id}/signatures`); setDetail({ doc, ...s }); } catch {} }

  const q = search.trim().toLowerCase();
  const searching = !!q;
  const matches = d => `${d.title || ''} ${d.category || ''} ${d.description || ''}`.toLowerCase().includes(q);
  const activeCard = DOC_CATEGORIES.find(c => c.key === cat);
  const shown = searching ? docs.filter(matches) : (activeCard ? docs.filter(d => bucketOf(d.category) === cat) : []);

  return (
    <div>
      {/* Search — pinned at the top, searches every category */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search all safety documents…" style={{ ...inp, maxWidth: 360 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setLibOpen(true)} style={{ background: '#fff', border: '1px solid #1e3a5f', color: '#1e3a5f', fontWeight: 700, fontSize: 13, borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>📚 Safety Policy Library</button>
          <button className="admin-primary-btn" onClick={() => { setForm({ title: '', category: cat || 'Safety Policies', description: '', body: '', file: null }); setErr(''); setAdding(true); }}>+ Add Document</button>
        </div>
      </div>

      {loading ? <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        : (!searching && !activeCard) ? (
          // Three category cards
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {DOC_CATEGORIES.map(c => {
              const count = docs.filter(d => bucketOf(d.category) === c.key).length;
              return (
                <button key={c.key} onClick={() => setCat(c.key)}
                  style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${c.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .15s, transform .1s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ fontSize: 30 }}>{c.icon}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{c.label}</div>
                  <div style={{ fontSize: 13, color: c.color, fontWeight: 600 }}>{c.sub}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{count} document{count !== 1 ? 's' : ''}</div>
                </button>
              );
            })}
          </div>
        ) : (
          // Document list: search results, or a chosen category
          <div>
            {!searching && activeCard && (
              <button onClick={() => setCat(null)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>‹ All Safety Documents</button>
            )}
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>{searching ? `${shown.length} result${shown.length !== 1 ? 's' : ''} for “${search.trim()}”` : `${activeCard.icon} ${activeCard.label} — ${shown.length} document${shown.length !== 1 ? 's' : ''}`}</div>
            <div className="admin-card" style={{ overflow: 'hidden' }}>
              {shown.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{searching ? 'No matching documents.' : 'No documents in this category yet.'}</div>
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table">
                      <thead><tr><th>Document</th><th>Category</th><th>Signed</th><th>Added</th><th style={{ width: 120 }}></th></tr></thead>
                      <tbody>
                        {shown.map(d => {
                          const done = d.worker_count > 0 && d.signed_count >= d.worker_count;
                          return (
                            <tr key={d.id}>
                              <td style={{ fontWeight: 600 }}>📄 {d.title}</td>
                              <td className="admin-list-secondary">{d.category || '—'}</td>
                              <td><span style={{ fontSize: 11, fontWeight: 700, color: done ? '#15803d' : '#b45309', background: done ? '#f0fdf4' : '#fffbeb', padding: '2px 8px', borderRadius: 10 }}>{d.signed_count} / {d.worker_count}</span></td>
                              <td className="admin-list-secondary">{fmtDate(d.created_at)}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {d.body && <button onClick={() => setReading(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>Read</button>}
                                {d.filename && <a href={photoUrl(d.filename)} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontSize: 12, fontWeight: 600, textDecoration: 'none', marginLeft: d.body ? 8 : 0 }}>View</a>}
                                <button onClick={() => openSigs(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600, marginLeft: 8 }}>Signatures</button>
                                <button onClick={() => del(d.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 6 }}>×</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </div>
        )}

      {adding && (
        <div style={modalWrap} onClick={() => setAdding(false)}>
          <div style={{ ...modalBox, width: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ ...modalHead, background: '#1e3a5f' }}><span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📄 Add Safety Document</span><button onClick={() => setAdding(false)} style={modalX}>×</button></div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><div style={lblS}>Title</div><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inp} placeholder="e.g. Trenching & Excavation Policy" /></div>
              <div><div style={lblS}>Category</div><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inp}>{DOC_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</select></div>
              <div><div style={lblS}>Description <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>(short summary)</span></div><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></div>
              <div><div style={lblS}>Written Content <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>(the full policy crews read in-app — optional if a file is attached)</span></div><textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={10} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} placeholder="Paste or write the policy text here…" /></div>
              <div><div style={lblS}>File (PDF / image) <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>(optional)</span></div><input type="file" accept="application/pdf,image/*" onChange={e => setForm(f => ({ ...f, file: e.target.files[0] }))} style={{ fontSize: 13 }} /></div>
              {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setAdding(false)} style={btnGhost}>Cancel</button>
                <button onClick={upload} disabled={saving} style={btnPrimary}>{saving ? 'Uploading…' : 'Add'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div style={modalWrap} onClick={() => setDetail(null)}>
          <div style={{ ...modalBox, width: 620 }} onClick={e => e.stopPropagation()}>
            <div style={{ ...modalHead, background: '#1e3a5f' }}><span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📄 {detail.doc.title}</span><button onClick={() => setDetail(null)} style={modalX}>×</button></div>
            <div style={{ padding: 20, display: 'flex', gap: 24 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 10 }}><span style={{ background: '#dcfce7', borderRadius: 99, padding: '2px 8px' }}>✓ Signed ({detail.signed.length})</span></div>
                {detail.signed.length === 0 ? <p style={{ fontSize: 13, color: '#9ca3af' }}>None yet.</p> : detail.signed.map(s => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div><div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div><div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(s.signed_at)}</div></div>
                    {s.signature && <img src={s.signature} alt="signature" style={{ height: 30, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff' }} />}
                  </div>
                ))}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 10 }}><span style={{ background: '#fee2e2', borderRadius: 99, padding: '2px 8px' }}>✗ Pending ({detail.pending.length})</span></div>
                {detail.pending.length === 0 ? <p style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>Everyone's signed 🎉</p> : detail.pending.map(w => (
                  <div key={w.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 600, color: '#374151' }}>{w.name}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Safety Policy Library picker */}
      {libOpen && (
        <div style={modalWrap} onClick={() => setLibOpen(false)}>
          <div style={{ ...modalBox, width: 640, maxHeight: '86vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...modalHead, background: '#1e3a5f' }}><span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📚 Safety Policy Library</span><button onClick={() => setLibOpen(false)} style={modalX}>×</button></div>
            <div style={{ padding: 20, overflowY: 'auto' }}>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.5 }}>Prewritten, OSHA-aligned starter programs. Pick one to load it into a new document you can review, edit, and save. Crews read and sign it in-app. <strong>Review and tailor each policy with your competent person before use — these are templates, not certified compliance documents.</strong></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {SAFETY_POLICY_LIBRARY.map(p => (
                  <div key={p.key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{p.title}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{p.summary}</div>
                    </div>
                    <button onClick={() => { setForm({ title: p.title, category: p.category, description: p.summary, body: p.body, file: null }); setLibOpen(false); setErr(''); setAdding(true); }}
                      style={{ ...btnPrimary, whiteSpace: 'nowrap' }}>Use this →</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Read a written policy */}
      {reading && (
        <div style={modalWrap} onClick={() => setReading(null)}>
          <div style={{ ...modalBox, width: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...modalHead, background: '#1e3a5f' }}><span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📄 {reading.title}</span><button onClick={() => setReading(null)} style={modalX}>×</button></div>
            <div style={{ padding: 24, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6, color: '#1f2937' }}>{reading.body}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Job Hazard Analyses ─────────────────────────────────────────────────────
const EMPTY_JHA = { title: '', project_id: '', task: '', steps: [{ step: '', hazard: '', control: '' }], status: 'active' };
function JobHazardAnalyses() {
  const [jhas, setJhas] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_JHA);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [sigs, setSigs] = useState([]);

  function reload() { apiFetch('/jha').then(d => setJhas(Array.isArray(d) ? d : [])).catch(() => {}); }
  async function downloadPdf(id) {
    try {
      const res = await fetch(`${API_BASE}/jha/${id}/pdf`, { headers: { 'x-worker-token': getToken() } });
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'JHA.pdf'; a.click(); URL.revokeObjectURL(url);
    } catch {}
  }
  useEffect(() => {
    Promise.all([apiFetch('/jha').catch(() => []), apiFetch('/projects').catch(() => [])])
      .then(([j, p]) => { setJhas(Array.isArray(j) ? j : []); setJobs(Array.isArray(p) ? p : []); }).finally(() => setLoading(false));
  }, []);

  function startNew() { setForm(EMPTY_JHA); setSigs([]); setEditId('new'); setErr(''); }
  function open(j) {
    let steps = []; try { steps = JSON.parse(j.steps || '[]'); } catch {}
    if (!steps.length) steps = [{ step: '', hazard: '', control: '' }];
    setForm({ title: j.title || '', project_id: j.project_id || '', task: j.task || '', steps, status: j.status || 'active' }); setEditId(j.id); setErr('');
    setSigs([]); apiFetch(`/jha/${j.id}`).then(full => setSigs(full.signatures || [])).catch(() => {});
  }
  const setStep = (i, k, v) => setForm(f => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { step: '', hazard: '', control: '' }] }));
  const removeStep = i => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const stepsAreBlank = steps => steps.every(s => !s.step && !s.hazard && !s.control);
  function pickType(type) {
    setForm(f => {
      const tmpl = JHA_TEMPLATES[type];
      const load = tmpl && stepsAreBlank(f.steps);
      return { ...f, task: type, title: (f.title && !JHA_TYPES.includes(f.title)) ? f.title : type, steps: load ? tmpl.map(s => ({ ...s })) : f.steps };
    });
  }
  function loadTemplate() {
    const tmpl = JHA_TEMPLATES[form.task];
    if (!tmpl) return;
    if (!stepsAreBlank(form.steps) && !window.confirm(`Replace the current steps with the ${form.task} template?`)) return;
    setForm(f => ({ ...f, steps: tmpl.map(s => ({ ...s })) }));
  }

  async function save() {
    if (!form.title.trim()) { setErr('Add a title'); return; }
    setSaving(true); setErr('');
    try {
      const isNew = editId === 'new';
      const body = { title: form.title, project_id: form.project_id || null, task: form.task, steps: form.steps.filter(s => s.step || s.hazard || s.control), status: form.status };
      await apiFetch(isNew ? '/jha' : `/jha/${editId}`, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(body) });
      reload(); setEditId(null);
    } catch (e) { setErr('Save failed'); }
    setSaving(false);
  }
  async function del(id) { if (!window.confirm('Delete this JHA?')) return; try { await apiFetch(`/jha/${id}`, { method: 'DELETE' }); setJhas(p => p.filter(x => x.id !== id)); setEditId(null); } catch {} }

  const th = { padding: '7px 10px', fontWeight: 700, fontSize: 11, color: '#fff', textAlign: 'left', textTransform: 'uppercase', background: '#b45309' };
  const td = { padding: '6px 8px', verticalAlign: 'top' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Break a task into steps → hazards → controls. {jhas.length} JHA{jhas.length !== 1 ? 's' : ''}.</div>
        <button className="admin-primary-btn" onClick={startNew}>+ New JHA</button>
      </div>

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          : jhas.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No JHAs yet.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead><tr><th>Title</th><th>Job</th><th>Task</th><th>Steps</th><th>Created</th><th style={{ width: 70 }}></th></tr></thead>
                <tbody>
                  {jhas.map(j => { let steps = []; try { steps = JSON.parse(j.steps || '[]'); } catch {} return (
                    <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => open(j)}>
                      <td style={{ fontWeight: 600 }}>⚠️ {j.title}</td>
                      <td className="admin-list-secondary">{j.project_number || '—'}</td>
                      <td className="admin-list-secondary">{j.task || '—'}</td>
                      <td className="admin-list-secondary">{steps.length}</td>
                      <td className="admin-list-secondary">{fmtDate(j.created_at)}</td>
                      <td onClick={e => e.stopPropagation()}><button onClick={() => open(j)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>Open</button><button onClick={() => del(j.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 4 }}>×</button></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {editId && (
        <div style={modalWrap} onClick={() => setEditId(null)}>
          <div style={{ ...modalBox, width: 760 }} onClick={e => e.stopPropagation()}>
            <div style={{ ...modalHead, background: '#b45309' }}><span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>⚠️ {editId === 'new' ? 'New JHA' : 'Job Hazard Analysis'}</span><button onClick={() => setEditId(null)} style={modalX}>×</button></div>
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div style={{ gridColumn: '1 / -1' }}><div style={lblS}>Title</div><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inp} placeholder="e.g. Setting light pole bases" /></div>
                <div><div style={lblS}>Job (optional)</div><select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} style={inp}><option value="">— None —</option>{jobs.map(p => <option key={p.id} value={p.id}>{jobLabel(p)}</option>)}</select></div>
                <div><div style={lblS}>Task type</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select value={JHA_TYPES.includes(form.task) ? form.task : ''} onChange={e => pickType(e.target.value)} style={inp}>
                      <option value="">— Select task —</option>
                      {JHA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {JHA_TEMPLATES[form.task] && <button type="button" onClick={loadTemplate} title="Load the starter procedure for this task" style={{ whiteSpace: 'nowrap', padding: '0 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f8fafc', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>↻ Template</button>}
                  </div>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr><th style={{ ...th, width: 30 }}>#</th><th style={th}>Step</th><th style={th}>Hazard</th><th style={th}>Control</th><th style={{ ...th, width: 30 }} /></tr></thead>
                  <tbody>
                    {form.steps.map((s, i) => (
                      <tr key={i} style={{ background: i % 2 ? '#fffbeb' : '#fff' }}>
                        <td style={{ ...td, textAlign: 'center', color: '#9ca3af', fontWeight: 700 }}>{i + 1}</td>
                        <td style={td}><textarea value={s.step} onChange={e => setStep(i, 'step', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></td>
                        <td style={td}><textarea value={s.hazard} onChange={e => setStep(i, 'hazard', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></td>
                        <td style={td}><textarea value={s.control} onChange={e => setStep(i, 'control', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></td>
                        <td style={{ ...td, textAlign: 'center' }}>{form.steps.length > 1 && <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16 }}>×</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={addStep} style={{ marginTop: 8, padding: '6px 12px', borderRadius: 7, border: '1px dashed #cbd5e1', background: '#fffbeb', color: '#b45309', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Step</button>

              {editId !== 'new' && (
                <div style={{ marginTop: 18, borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Crew Sign-Off ({sigs.length})</div>
                    <button onClick={() => downloadPdf(editId)} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📄 Download PDF</button>
                  </div>
                  {sigs.length === 0 ? <div style={{ fontSize: 12, color: '#9ca3af' }}>No signatures yet.</div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{sigs.map(s => (
                        <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                          <div><span style={{ fontSize: 13, fontWeight: 600 }}>{s.name || '—'}</span><span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>{fmtDate(s.signed_at)}</span></div>
                          {s.signature && <img src={s.signature} alt="" style={{ height: 26, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff' }} />}
                        </div>
                      ))}</div>}
                </div>
              )}

              {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{err}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 16 }}>
                {editId !== 'new' ? <button onClick={() => del(editId)} style={{ ...btnGhost, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button> : <span />}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setEditId(null)} style={btnGhost}>Cancel</button>
                  <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save JHA'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const modalWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalBox = { background: '#fff', borderRadius: 14, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' };
const modalHead = { borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const modalX = { background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' };
const btnGhost = { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' };
const btnPrimary = { padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
